class AIRecommendationService {
  constructor() {
    this.studyTechniques = [
      {
        id: 'pomodoro',
        name: 'Pomodoro Technique',
        description: 'Study in 25-minute focused sessions with 5-minute breaks',
        icon: '⏰',
        difficulty: 'beginner',
        subjects: ['all'],
        timeRequired: 25,
        effectiveness: 0.9
      },
      {
        id: 'spaced_repetition',
        name: 'Spaced Repetition',
        description: 'Review material at increasing intervals for better retention',
        icon: '🔄',
        difficulty: 'intermediate',
        subjects: ['mathematics', 'physics', 'chemistry', 'biology'],
        timeRequired: 15,
        effectiveness: 0.95
      },
      {
        id: 'active_recall',
        name: 'Active Recall',
        description: 'Test yourself instead of just re-reading material',
        icon: '🧠',
        difficulty: 'intermediate',
        subjects: ['all'],
        timeRequired: 20,
        effectiveness: 0.92
      },
      {
        id: 'feynman',
        name: 'Feynman Technique',
        description: 'Explain concepts in simple terms to identify knowledge gaps',
        icon: '💡',
        difficulty: 'advanced',
        subjects: ['physics', 'mathematics', 'chemistry'],
        timeRequired: 30,
        effectiveness: 0.88
      },
      {
        id: 'mind_mapping',
        name: 'Mind Mapping',
        description: 'Create visual diagrams to connect related concepts',
        icon: '🗺️',
        difficulty: 'beginner',
        subjects: ['biology', 'history', 'literature'],
        timeRequired: 20,
        effectiveness: 0.85
      },
      {
        id: 'interleaving',
        name: 'Interleaving',
        description: 'Mix different subjects or topics in one study session',
        icon: '🔄',
        difficulty: 'intermediate',
        subjects: ['all'],
        timeRequired: 45,
        effectiveness: 0.87
      }
    ];

    this.breakActivities = [
      {
        id: 'stretching',
        name: 'Quick Stretching',
        description: 'Simple stretches to relieve tension',
        icon: '🧘',
        duration: 5,
        energy: 'low'
      },
      {
        id: 'walking',
        name: 'Short Walk',
        description: 'Take a brief walk to refresh your mind',
        icon: '🚶',
        duration: 10,
        energy: 'medium'
      },
      {
        id: 'hydration',
        name: 'Hydration Break',
        description: 'Drink water and rest your eyes',
        icon: '💧',
        duration: 3,
        energy: 'low'
      },
      {
        id: 'deep_breathing',
        name: 'Deep Breathing',
        description: 'Practice mindful breathing exercises',
        icon: '🫁',
        duration: 5,
        energy: 'low'
      },
      {
        id: 'eye_rest',
        name: 'Eye Rest',
        description: '20-20-20 rule: Look 20ft away for 20 seconds',
        icon: '👁️',
        duration: 2,
        energy: 'low'
      }
    ];
  }

  /**
   * Get personalized study recommendations
   */
  getStudyRecommendations(habitData, studyPlan = null) {
    const recommendations = [];
    const analytics = this.getAnalytics(habitData);
    
    // Technique recommendations based on study patterns
    const techniqueRecs = this.getTechniqueRecommendations(analytics);
    recommendations.push(...techniqueRecs);
    
    // Subject prioritization
    const subjectRecs = this.getSubjectPrioritization(analytics, studyPlan);
    recommendations.push(...subjectRecs);
    
    // Break management
    const breakRecs = this.getBreakRecommendations(analytics);
    recommendations.push(...breakRecs);
    
    // Performance optimization
    const performanceRecs = this.getPerformanceRecommendations(analytics);
    recommendations.push(...performanceRecs);
    
    return recommendations;
  }

  /**
   * Get study technique recommendations
   */
  getTechniqueRecommendations(analytics) {
    const recommendations = [];
    
    // Check session length patterns
    if (analytics.averageSessionLength > 2) {
      recommendations.push({
        type: 'technique',
        priority: 'high',
        title: 'Try Shorter Sessions',
        description: 'Your sessions are quite long. Consider the Pomodoro Technique for better focus.',
        technique: this.studyTechniques.find(t => t.id === 'pomodoro'),
        reason: 'long_sessions'
      });
    }
    
    // Check subject variety
    if (analytics.subjectCount < 2) {
      recommendations.push({
        type: 'technique',
        priority: 'medium',
        title: 'Mix Your Subjects',
        description: 'Try interleaving different subjects to improve retention.',
        technique: this.studyTechniques.find(t => t.id === 'interleaving'),
        reason: 'low_variety'
      });
    }
    
    // Check completion rate
    if (analytics.completionRate < 0.8) {
      recommendations.push({
        type: 'technique',
        priority: 'high',
        title: 'Improve Focus',
        description: 'Use active recall to stay engaged and complete your sessions.',
        technique: this.studyTechniques.find(t => t.id === 'active_recall'),
        reason: 'low_completion'
      });
    }
    
    return recommendations;
  }

  /**
   * Get subject prioritization recommendations
   */
  getSubjectPrioritization(analytics, studyPlan) {
    const recommendations = [];
    
    if (!analytics.subjectBreakdown || Object.keys(analytics.subjectBreakdown).length === 0) {
      return recommendations;
    }
    
    // Find subjects with low study time
    const subjects = Object.entries(analytics.subjectBreakdown);
    const avgHours = subjects.reduce((sum, [, data]) => sum + data.hours, 0) / subjects.length;
    
    const lowPrioritySubjects = subjects
      .filter(([, data]) => data.hours < avgHours * 0.7)
      .map(([subject]) => subject);
    
    if (lowPrioritySubjects.length > 0) {
      recommendations.push({
        type: 'prioritization',
        priority: 'medium',
        title: 'Subject Balance',
        description: `Consider spending more time on: ${lowPrioritySubjects.join(', ')}`,
        subjects: lowPrioritySubjects,
        reason: 'imbalanced_subjects'
      });
    }
    
    // Check for exam proximity if study plan exists
    if (studyPlan && studyPlan.exam_dates) {
      const urgentSubjects = this.getUrgentSubjects(studyPlan.exam_dates, analytics.subjectBreakdown);
      if (urgentSubjects.length > 0) {
        recommendations.push({
          type: 'prioritization',
          priority: 'high',
          title: 'Exam Preparation',
          description: `Focus on these subjects with upcoming exams: ${urgentSubjects.join(', ')}`,
          subjects: urgentSubjects,
          reason: 'exam_proximity'
        });
      }
    }
    
    return recommendations;
  }

  /**
   * Get break management recommendations
   */
  getBreakRecommendations(analytics) {
    const recommendations = [];
    
    // Check for long sessions without breaks
    if (analytics.averageSessionLength > 1.5) {
      recommendations.push({
        type: 'break',
        priority: 'high',
        title: 'Take Regular Breaks',
        description: 'Schedule breaks every 25-30 minutes to maintain focus.',
        activities: this.breakActivities.filter(a => a.duration <= 10),
        reason: 'long_sessions_no_breaks'
      });
    }
    
    // Check for eye strain (long sessions)
    if (analytics.averageSessionLength > 2) {
      recommendations.push({
        type: 'break',
        priority: 'medium',
        title: 'Eye Care',
        description: 'Practice the 20-20-20 rule to prevent eye strain.',
        activities: this.breakActivities.filter(a => a.id === 'eye_rest'),
        reason: 'eye_strain_risk'
      });
    }
    
    return recommendations;
  }

  /**
   * Get performance optimization recommendations
   */
  getPerformanceRecommendations(analytics) {
    const recommendations = [];
    
    // Check study consistency
    if (analytics.studyDaysThisMonth < 15) {
      recommendations.push({
        type: 'performance',
        priority: 'high',
        title: 'Improve Consistency',
        description: 'Try to study at least 5 days per week for better results.',
        action: 'increase_frequency',
        reason: 'low_consistency'
      });
    }
    
    // Check session timing
    const peakHours = this.getPeakStudyHours(analytics);
    if (peakHours.length > 0) {
      recommendations.push({
        type: 'performance',
        priority: 'medium',
        title: 'Optimal Study Times',
        description: `Your most productive hours are: ${peakHours.join(', ')}`,
        action: 'schedule_peak_hours',
        reason: 'peak_performance_times'
      });
    }
    
    return recommendations;
  }

  /**
   * Get analytics from habit data
   */
  getAnalytics(habitData) {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const recentSessions = habitData.studyHistory.filter(session => 
      new Date(session.date) >= thirtyDaysAgo
    );
    
    const analytics = {
      totalSessions: recentSessions.length,
      totalHours: recentSessions.reduce((sum, session) => sum + session.duration, 0),
      averageSessionLength: recentSessions.length > 0 ? 
        recentSessions.reduce((sum, session) => sum + session.duration, 0) / recentSessions.length : 0,
      studyDaysThisMonth: new Set(recentSessions.map(s => 
        new Date(s.date).toDateString()
      )).size,
      completionRate: recentSessions.length > 0 ? 
        recentSessions.filter(s => s.completed).length / recentSessions.length : 0,
      subjectBreakdown: this.getSubjectBreakdown(recentSessions),
      subjectCount: new Set(recentSessions.flatMap(s => s.subjects)).size,
      weeklyTrend: this.getWeeklyTrend(recentSessions)
    };
    
    return analytics;
  }

  /**
   * Get subject breakdown
   */
  getSubjectBreakdown(sessions) {
    const subjectData = {};
    sessions.forEach(session => {
      session.subjects.forEach(subject => {
        if (!subjectData[subject]) {
          subjectData[subject] = { sessions: 0, hours: 0 };
        }
        subjectData[subject].sessions++;
        subjectData[subject].hours += session.duration;
      });
    });
    return subjectData;
  }

  /**
   * Get weekly trend
   */
  getWeeklyTrend(sessions) {
    const weeklyData = {};
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    daysOfWeek.forEach(day => {
      weeklyData[day] = 0;
    });
    
    sessions.forEach(session => {
      const day = daysOfWeek[new Date(session.date).getDay()];
      weeklyData[day] += session.duration;
    });
    
    return weeklyData;
  }

  /**
   * Get urgent subjects based on exam dates
   */
  getUrgentSubjects(examDates, subjectBreakdown) {
    const now = new Date();
    const urgentSubjects = [];
    
    Object.entries(examDates).forEach(([subject, examDate]) => {
      const daysUntilExam = Math.ceil((new Date(examDate) - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExam <= 14 && daysUntilExam > 0) {
        // Check if subject needs more attention
        const currentHours = subjectBreakdown[subject]?.hours || 0;
        if (currentHours < 10) { // Less than 10 hours studied
          urgentSubjects.push(subject);
        }
      }
    });
    
    return urgentSubjects;
  }

  /**
   * Get peak study hours
   */
  getPeakStudyHours(analytics) {
    // This would typically analyze session timing patterns
    // For now, return common peak hours
    return ['9:00 AM - 11:00 AM', '2:00 PM - 4:00 PM', '7:00 PM - 9:00 PM'];
  }

  /**
   * Get optimal study schedule
   */
  getOptimalSchedule(habitData, studyPlan = null) {
    const analytics = this.getAnalytics(habitData);
    const schedule = {
      recommendedTechniques: [],
      breakSchedule: [],
      subjectPriority: [],
      optimalTimes: []
    };
    
    // Recommend techniques based on patterns
    if (analytics.averageSessionLength > 1.5) {
      schedule.recommendedTechniques.push('pomodoro');
    }
    
    if (analytics.subjectCount < 2) {
      schedule.recommendedTechniques.push('interleaving');
    }
    
    // Break schedule
    if (analytics.averageSessionLength > 1) {
      schedule.breakSchedule = [
        { after: 25, activity: 'hydration' },
        { after: 50, activity: 'stretching' },
        { after: 75, activity: 'eye_rest' }
      ];
    }
    
    // Subject priority based on exam dates and current progress
    if (studyPlan && studyPlan.exam_dates) {
      schedule.subjectPriority = this.getSubjectPriority(studyPlan.exam_dates, analytics.subjectBreakdown);
    }
    
    // Optimal study times
    schedule.optimalTimes = this.getPeakStudyHours(analytics);
    
    return schedule;
  }

  /**
   * Get subject priority list
   */
  getSubjectPriority(examDates, subjectBreakdown) {
    const now = new Date();
    const subjects = Object.keys(examDates);
    
    return subjects.sort((a, b) => {
      const daysA = Math.ceil((new Date(examDates[a]) - now) / (1000 * 60 * 60 * 24));
      const daysB = Math.ceil((new Date(examDates[b]) - now) / (1000 * 60 * 60 * 24));
      const hoursA = subjectBreakdown[a]?.hours || 0;
      const hoursB = subjectBreakdown[b]?.hours || 0;
      
      // Prioritize by exam proximity and study time needed
      const priorityA = daysA - (hoursA / 2); // Less days + less study time = higher priority
      const priorityB = daysB - (hoursB / 2);
      
      return priorityA - priorityB;
    });
  }

  /**
   * Get personalized study tips
   */
  getPersonalizedTips(habitData) {
    const analytics = this.getAnalytics(habitData);
    const tips = [];
    
    if (analytics.averageSessionLength < 0.5) {
      tips.push({
        tip: "Try studying for at least 30 minutes per session for better retention",
        category: "session_length",
        priority: "high"
      });
    }
    
    if (analytics.completionRate < 0.8) {
      tips.push({
        tip: "Set smaller, achievable goals to improve completion rates",
        category: "completion",
        priority: "high"
      });
    }
    
    if (analytics.studyDaysThisMonth < 15) {
      tips.push({
        tip: "Study consistently - even 30 minutes daily is better than 3 hours once a week",
        category: "consistency",
        priority: "medium"
      });
    }
    
    if (analytics.subjectCount < 2) {
      tips.push({
        tip: "Vary your subjects to keep your brain engaged and improve retention",
        category: "variety",
        priority: "medium"
      });
    }
    
    return tips;
  }

  /**
   * Get achievements for demo purposes
   */
  getAchievements() {
    return [
      {
        id: 'first_session',
        title: 'First Study Session',
        description: 'Completed your first study session',
        icon: '🎯',
        type: 'milestone',
        coins: 10
      },
      {
        id: 'streak_3',
        title: '3-Day Streak',
        description: 'Studied for 3 consecutive days',
        icon: '🔥',
        type: 'streak',
        coins: 25
      },
      {
        id: 'subject_master',
        title: 'Subject Master',
        description: 'Studied 5 different subjects',
        icon: '📚',
        type: 'variety',
        coins: 50
      }
    ];
  }
}

export default new AIRecommendationService(); 